<template>
  <div class="konzept">
    <!-- ============ Header ============ -->
    <section class="konzept-hero">
      <div class="lb-container lb-container--narrow konzept-hero__inner">
        <SectionLabel text="Konzept & Über mich" />
        <h1>
          Eine klare Empfehlung.
          <span class="lb-handwritten">Statt einer langen Liste.</span>
        </h1>
        <p class="konzept-hero__lead">
          Ich bin Kilian Link. Spezialist für Zahnzusatzversicherungen. Mein Job ist
          es, Ihnen <strong>genau einen Tarif</strong> zu empfehlen — und Ihnen
          zu erklären, warum dieser zu Ihrer Situation passt.
        </p>
      </div>
    </section>

    <!-- ============ About ============ -->
    <section class="lb-section konzept-about">
      <div class="lb-container konzept-about__grid">
        <div class="konzept-about__image">
          <div class="konzept-portrait">
            <img
              :src="kili2Img"
              alt="Kilian Link — Versicherungsvermittler, Büro München-Untergiesing"
              class="konzept-portrait-img"
            />
          </div>
          <div class="konzept-about__caption">
            <SparkleIcon :size="18" color="#F59E0B" />
            <span>Kilian Link · 47 · Büro München-Untergiesing</span>
          </div>
        </div>

        <div class="konzept-about__copy">
          <h2>Hallo, ich bin Kilian.</h2>
          <p>
            Seit über <strong>15 Jahren</strong> beschäftige ich mich
            ausschließlich mit Zahnzusatzversicherungen. Ich habe gesehen, wie
            schwierig es für viele Menschen ist, die richtige Entscheidung zu
            treffen. Also den individuell passenden Zahn-Tarif zu finden.
          </p>
          <p>
            Ich berate Menschen, die <strong>keine Lust auf komplizierte
              Vergleichsrechner</strong> haben, sondern eine verständliche und
            persönliche Empfehlung möchten. Versicherungen müssen nicht
            kompliziert erklärt werden.
          </p>

          <div class="konzept-quote">
            <p>
              Deshalb mache ich es einfach:
              <br />
              Ich analysiere Ihre Situation und empfehle Ihnen genau den Tarif,
              der passt.
            </p>
            <p class="konzept-quote__sign">
              <span class="lb-handwritten">Ohne Umwege. Ohne Verwirrung.</span>
            </p>
          </div>

          <h3>Meine Qualifikation</h3>
          <ul class="konzept-credentials">
            <li><span class="check">✔</span> Spezialist für Zahnzusatzversicherungen</li>
            <li><span class="check">✔</span> Über 15 Jahre Erfahrung in der Tarif-Analyse</li>
            <li><span class="check">✔</span> Geprüfter Versicherungsvermittler IHK (§ 34d GewO)</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- ============ Method deep ============ -->
    <section class="lb-section lb-section--cream konzept-method">
      <div class="lb-container">
        <div class="konzept-method__head">
          <SectionLabel text="So arbeite ich" />
          <h2>Die LinkMethode™ im Detail</h2>
          <p class="konzept-method__lead">
            Ein System, das dafür entwickelt wurde, <strong>schnell und
              sicher</strong> den für Sie passenden Zahn-Tarif zu finden.
          </p>
        </div>

        <div class="konzept-method__row" v-for="step in steps" :key="step.num">
          <div class="konzept-method__num">{{ step.num }}</div>
          <div class="konzept-method__content">
            <h3>{{ step.title }}</h3>
            <p>{{ step.text }}</p>
            <ul>
              <li v-for="b in step.bullets" :key="b">
                <span class="check">✔</span> {{ b }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ Waizmann ============ -->
    <section class="lb-section konzept-waizmann">
      <div class="lb-container lb-container--narrow konzept-waizmann__inner">
        <SectionLabel text="Meine Grundlage" />
        <h2>Ich arbeite mit der WaizmannTabelle.</h2>
        <p>
          Über <strong>11.000 Zahnärzte</strong> in Deutschland empfehlen
          bereits die WaizmannTabelle. Das Original — seit 1995.
        </p>
        <p>
          Für meine Empfehlungen nutze ich diese bewährten Analysen. So ist
          jede Empfehlung auf einer fundierten, transparenten Grundlage gebaut.
        </p>
      </div>
    </section>

    <!-- ============ Final CTA ============ -->
    <section class="lb-section lb-section--dark konzept-cta">
      <div class="lb-container lb-container--narrow konzept-cta__inner">
        <SectionLabel text="Bereit für Klarheit?" />
        <h2>Lassen Sie uns telefonieren.</h2>
        <p>
          10 bis 15 Minuten reichen, damit Sie eine klare Empfehlung in der Hand
          haben.
        </p>
        <CtaButton to="/sprechstunde" size="xl">
          Termin jetzt buchen
        </CtaButton>
      </div>
    </section>
  </div>
</template>

<script setup>
import { onMounted, inject } from "vue";
import SectionLabel from "@/components/ui/SectionLabel.vue";
import CtaButton from "@/components/ui/CtaButton.vue";
import SparkleIcon from "@/components/ui/SparkleIcon.vue";
import kili2Img from "@/assets/Kili2.png";

const updateDocumentHead = inject("updateDocumentHead", null);

const steps = [
  {
    num: "1",
    title: "Analyse Ihrer Situation",
    text:
      "Wir sprechen über Ihre Zähne, mögliche Vorerkrankungen, Ihr Alter und Ihre Erwartungen an die Versicherung.",
    bullets: [
      "Ihre individuellen Gegebenheiten",
      "Ihre Wünsche an Leistungen und Kosten",
      "Ihre Zeitplanung",
    ],
  },
  {
    num: "2",
    title: "Bewertung der besten Tarife",
    text:
      "Auf Basis der WaizmannTabelle gleiche ich Ihre Situation mit den aktuell besten Tarifen am Markt ab.",
    bullets: [
      "Aktuelle Tarifdaten",
      "Wartezeiten und Zahnersatz-Leistungen geprüft",
      "Preis-Leistung im Vergleich",
    ],
  },
  {
    num: "3",
    title: "Klare Empfehlung",
    text:
      "Sie bekommen genau einen Tarif empfohlen — verständlich begründet. Keine Auswahl, sondern eine Entscheidung.",
    bullets: [
      "Empfehlung mit Begründung",
      "Unterschriftsreife Unterlagen per Post oder E-Mail",
      "Sie entscheiden in Ruhe",
    ],
  },
];

onMounted(() => {
  if (updateDocumentHead) {
    updateDocumentHead(
      "Konzept & Über mich — Kilian Link | LinkBeratung",
      "Die LinkMethode™ im Detail. Kilian Link, geprüfter Versicherungsvermittler IHK, erklärt sein Vorgehen für eine klare Zahnzusatz-Empfehlung."
    );
  }
});
</script>

<style scoped>
.konzept-hero {
  background:
    radial-gradient(900px 500px at 80% -10%, rgba(108, 76, 241, 0.08), transparent 60%),
    #fff;
  padding: 80px 0 60px;
  text-align: center;
}

.konzept-hero__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.konzept-hero__inner h1 {
  margin: 12px 0 16px;
}

.konzept-hero__lead {
  font-size: clamp(18px, 1.5vw, 21px);
  color: var(--lb-ink-soft);
  max-width: 720px;
}

.konzept-about__grid {
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 64px;
  align-items: start;
}

.konzept-about__image {
  position: sticky;
  top: 110px;
}

.konzept-portrait {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  background: var(--lb-cream);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(11, 31, 59, 0.1);
}

.konzept-portrait-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
}

.konzept-about__caption {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Caveat", cursive;
  font-size: 22px;
  color: var(--lb-ink);
}

.konzept-quote {
  background: var(--lb-cream);
  border-left: 4px solid var(--lb-cta);
  padding: 22px 24px;
  border-radius: 12px;
  margin: 20px 0 32px;
}

.konzept-quote p {
  margin: 0;
}

.konzept-quote__sign {
  margin-top: 10px !important;
  font-size: 22px;
}

.konzept-credentials {
  list-style: none;
  padding: 0;
  margin: 12px 0 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 16px;
}

.check {
  color: var(--lb-cta);
  font-weight: 900;
  margin-right: 6px;
}

@media (max-width: 960px) {
  .konzept-about__grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .konzept-about__image {
    position: static;
    max-width: 420px;
    margin: 0 auto;
  }
}

.konzept-method__head {
  text-align: center;
  margin-bottom: 48px;
}

.konzept-method__head h2 {
  margin-top: 12px;
}

.konzept-method__lead {
  max-width: 720px;
  margin: 12px auto 0;
  font-size: 18px;
}

.konzept-method__row {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 28px;
  align-items: start;
  background: #fff;
  border-radius: 22px;
  padding: 32px;
  margin-bottom: 22px;
  border: 1px solid var(--lb-line);
}

.konzept-method__num {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--lb-accent);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Playfair Display", serif;
  font-size: 32px;
  font-weight: 800;
  box-shadow: 0 12px 24px rgba(108, 76, 241, 0.25);
}

.konzept-method__content h3 {
  margin-bottom: 10px;
}

.konzept-method__content ul {
  list-style: none;
  padding: 0;
  margin: 12px 0 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 16px;
  color: var(--lb-ink);
}

@media (max-width: 720px) {
  .konzept-method__row {
    grid-template-columns: 1fr;
    text-align: left;
  }
}

.konzept-waizmann__inner {
  text-align: center;
}

.konzept-waizmann__inner h2 {
  margin: 12px 0 18px;
}

.konzept-waizmann__inner p {
  font-size: 18px;
  max-width: 640px;
  margin: 0 auto 14px;
}

.konzept-cta__inner {
  text-align: center;
}

.konzept-cta__inner h2 {
  margin: 12px 0 16px;
  font-size: clamp(34px, 4.5vw, 56px);
}

.konzept-cta__inner p {
  font-size: 18px;
  margin-bottom: 30px;
}
</style>
